import { Container, Graphics, Rectangle, RenderTexture, Texture } from 'pixi.js';
import { ATLAS_MAX_PX } from '@enugu/shared';
import { packShelves } from './ShelfPacker';
function nextPow2(v) {
    let p = 1;
    while (p < v)
        p <<= 1;
    return p;
}
/**
 * Bakes procedurally drawn Graphics into atlas pages (RenderTextures), so the
 * placeholder art batches exactly like real atlas art would: every sprite of
 * a page shares one TextureSource. Real art arrives as pre-packed atlases
 * through AssetManager; this class only exists for generated content.
 */
export class AtlasBuilder {
    items = [];
    renderer;
    opts;
    constructor(renderer, opts) {
        this.renderer = renderer;
        this.opts = { padding: 2, mipmaps: true, label: 'atlas', ...opts };
    }
    add(item) {
        this.items.push(item);
    }
    get size() {
        return this.items.length;
    }
    build() {
        const { artScale, padding, mipmaps, label } = this.opts;
        const pageLogical = Math.floor(ATLAS_MAX_PX / artScale);
        const byKey = new Map();
        for (const it of this.items) {
            if (byKey.has(it.key))
                throw new Error(`AtlasBuilder: duplicate key "${it.key}"`);
            byKey.set(it.key, it);
        }
        const placements = packShelves(this.items.map((it) => ({ key: it.key, w: Math.ceil(it.trim?.w ?? it.w), h: Math.ceil(it.trim?.h ?? it.h) })), pageLogical, pageLogical, padding);
        const pageCount = placements.reduce((m, p) => Math.max(m, p.page + 1), 0);
        const textures = new Map();
        const sources = [];
        let gpuBytes = 0;
        for (let page = 0; page < pageCount; page++) {
            const onPage = placements.filter((p) => p.page === page);
            let usedW = 0;
            let usedH = 0;
            for (const p of onPage) {
                usedW = Math.max(usedW, p.x + p.w + padding);
                usedH = Math.max(usedH, p.y + p.h + padding);
            }
            // Power-of-two pixel sizes keep mipmapping valid everywhere and the GPU allocator happy.
            const pxW = Math.min(ATLAS_MAX_PX, nextPow2(Math.ceil(usedW * artScale)));
            const pxH = Math.min(ATLAS_MAX_PX, nextPow2(Math.ceil(usedH * artScale)));
            const rt = RenderTexture.create({
                width: pxW / artScale,
                height: pxH / artScale,
                resolution: artScale,
                autoGenerateMipmaps: mipmaps,
                scaleMode: 'linear',
                label: `${label}#${page}`,
            });
            const root = new Container();
            for (const p of onPage) {
                const it = byKey.get(p.key);
                const g = new Graphics();
                it.draw(g);
                g.position.set(p.x - (it.trim?.x ?? 0), p.y - (it.trim?.y ?? 0));
                root.addChild(g);
            }
            this.renderer.render({ container: root, target: rt, clear: true, clearColor: [0, 0, 0, 0] });
            if (mipmaps)
                rt.source.updateMipmaps();
            root.destroy({ children: true });
            sources.push(rt.source);
            gpuBytes += pxW * pxH * 4 * (mipmaps ? 4 / 3 : 1);
            for (const p of onPage) {
                const it = byKey.get(p.key);
                const tex = it.trim
                    ? new Texture({
                        source: rt.source,
                        frame: new Rectangle(p.x, p.y, it.trim.w, it.trim.h),
                        orig: new Rectangle(0, 0, it.w, it.h),
                        trim: new Rectangle(it.trim.x, it.trim.y, it.trim.w, it.trim.h),
                        defaultAnchor: { x: it.anchorX / it.w, y: it.anchorY / it.h },
                        label: it.key,
                    })
                    : new Texture({
                        source: rt.source,
                        frame: new Rectangle(p.x, p.y, it.w, it.h),
                        defaultAnchor: { x: it.anchorX / it.w, y: it.anchorY / it.h },
                        label: it.key,
                    });
                textures.set(it.key, tex);
            }
        }
        this.items.length = 0;
        return { textures, sources, gpuBytes, pages: pageCount };
    }
}
